<template>
    <div>
        <v-dialog v-model="importDialog"
                  persistent width="600px"
                  fullscreen
                  hide-overlay
                  transition="dialog-bottom-transition">
            <v-toolbar dark color="primary">
                <v-toolbar-title>Import de vos prestataires</v-toolbar-title>

                <div class="text-right w-100">
                    <v-btn icon dark @click="closeDialog">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </div>
            </v-toolbar>

            <v-stepper v-model="currentStep">
                <v-stepper-header>
                    <v-stepper-step step="1">Sélection fichier</v-stepper-step>

                    <v-divider></v-divider>

                    <v-stepper-step step="2">Vérifications</v-stepper-step>

                    <v-divider></v-divider>

                    <v-stepper-step step="3">Tags</v-stepper-step>
                </v-stepper-header>

                <v-stepper-items>
                    <v-stepper-content step="1">
                        <v-alert text class="mb-5">
                            <p>
                                Pour importer une liste de prestataires dans Dahub, utilisez le fichier exemple fourni :
                                <a class="mt-2" :href="apiUrl + '/dahub-providers-import'">
                                     Fichier exemple</a><br />
                            </p>

                            <v-expansion-panels>
                                <v-expansion-panel>
                                    <v-expansion-panel-header>Quelles sont les colonnes obligatoires ?
                                    </v-expansion-panel-header>
                                    <v-expansion-panel-content>
                                        <p>
                                            Le <code>Nom de l'offre</code>, la <code>Catégorie</code>, l'email
                                            <code>Destinataire</code>, la<code>Ville</code> et le
                                            <code>Code postal</code> sont obligatoires.
                                        </p>
                                    </v-expansion-panel-content>
                                </v-expansion-panel>

                                <v-expansion-panel>
                                    <v-expansion-panel-header>Quelles sont les colonnes facultatives ?
                                    </v-expansion-panel-header>
                                    <v-expansion-panel-content>
                                        <p>
                                            Le <code>Nom contact</code>, le <code>Prénom contact</code> et le
                                            <code>Téléphone</code>ne sont pas obligatoires.
                                            <br />
                                            L'<code>Id Dahub</code> / <code>Id Tourinsoft</code> sert si vous souhaiter
                                            modifier des fiches en volume.
                                        </p>
                                    </v-expansion-panel-content>
                                </v-expansion-panel>

                                <v-expansion-panel>
                                    <v-expansion-panel-header>Quelles sont les catégories disponibles ?
                                    </v-expansion-panel-header>
                                    <v-expansion-panel-content>
                                        <p>
                                            La liste est la suivante:
                                        </p>
                                        <ul>
                                            <li>Activité</li>
                                            <li>Équipement</li>
                                            <li>Hôtellerie de plein air</li>
                                            <li>Hébergement collectif</li>
                                            <li>Patrimoine culturel</li>
                                            <li>Restauration</li>
                                            <li>Commerce et service</li>
                                            <li>Producteur</li>
                                            <li>Hôtellerie</li>
                                            <li>Hébergement locatif</li>
                                            <li>Patrimoine naturel</li>
                                        </ul>
                                        <p>
                                            Pour plus d’explication concernant ces catégories, vous pouvez vous référer
                                            à cet article
                                            <a target="_blank"
                                               href="https://aide.apidae-tourisme.com/hc/fr/articles/360001513532-Les-types-objets">
                                                Apidae</a>.
                                        </p>
                                    </v-expansion-panel-content>
                                </v-expansion-panel>

                                <v-expansion-panel>
                                    <v-expansion-panel-header>Puis-je importer plusieurs fois des prestataires ?
                                    </v-expansion-panel-header>
                                    <v-expansion-panel-content>
                                        <p>
                                            Vous pouvez importer des prestataires autant de fois que nécessaire.<br />
                                            Si vous importez un prestataire déjà existant sur Dahub, nous mettrons
                                            automatiquement la fiche à jour (pour cela utiliser la colonne <code>Id
                                            Dahub</code>).
                                        </p>
                                    </v-expansion-panel-content>
                                </v-expansion-panel>
                            </v-expansion-panels>
                        </v-alert>

                        <div class="d-flex">
                            <v-file-input @change="loadFile"
                                          v-model="file"
                                          accept=".xls,.xlsx"
                                          label="Liste des prestataires (.xls ou .xlsx)"
                                          outlined
                                          :rules="rules"
                                          dense>
                            </v-file-input>
                        </div>

                        <v-alert v-if="error" type="error">
                            {{ error }}
                        </v-alert>

                        <div class="actions d-flex justify-content-between">
                            <v-btn color="red" style="color: white" @click="closeDialog">Annuler</v-btn>
                            <v-btn class="default-button ml-3 load-button"
                                   :disabled="file === null || !validFile"
                                   @click="loadProviders">
                                Charger
                                <v-icon>keyboard_arrow_right</v-icon>
                            </v-btn>
                        </div>
                    </v-stepper-content>

                    <v-stepper-content step="2" class="h-100">
                        <v-expansion-panels v-model="panel" mandatory>
                            <v-expansion-panel v-if="providersErrors.length">
                                <v-expansion-panel-header>
                                    <div class="table-title">
                                        <v-icon color="warning">warning</v-icon>
                                        Fiches en erreur ({{ providersErrors.length }})
                                    </div>
                                </v-expansion-panel-header>
                                <v-expansion-panel-content>
                                    <v-simple-table height="calc(100vh - 400px)" fixed-header>
                                        <thead>
                                        <tr>
                                            <th class="text-left">Nom de l'offre</th>
                                            <th class="text-left">Catégorie</th>
                                            <th class="text-left">Destinataire</th>
                                            <th class="text-left">Ville</th>
                                            <th class="text-left">Code postal</th>
                                            <th class="text-left">Identifiant Dahub</th>
                                            <th class="text-left">Erreur</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        <tr v-for="provider in providersErrors" :key="provider.id">
                                            <td>{{ provider.nomDeLoffre }}</td>
                                            <td>{{ provider.categorie }}</td>
                                            <td>{{ provider.destinataire }}</td>
                                            <td>{{ provider.ville }}</td>
                                            <td>{{ provider.codePostal }}</td>
                                            <td>{{ provider.idDahub }}</td>
                                            <td class="text-error">{{ provider.error.label }}</td>
                                        </tr>
                                        </tbody>
                                    </v-simple-table>
                                </v-expansion-panel-content>
                            </v-expansion-panel>

                            <v-expansion-panel v-if="providersToImport.length">
                                <v-expansion-panel-header>
                                    <div class="table-title">
                                        <v-icon color="success">check</v-icon>
                                        Fiches valides ({{ providersToImport.length }})
                                    </div>
                                </v-expansion-panel-header>
                                <v-expansion-panel-content>
                                    <v-simple-table height="calc(100vh - 400px)" fixed-header>
                                        <thead>
                                        <tr>
                                            <th class="text-left">Nom de l'offre</th>
                                            <th class="text-left">Catégorie</th>
                                            <th class="text-left">Destinataire</th>
                                            <th class="text-left">Téléphone</th>
                                            <th class="text-left">Nom du contact</th>
                                            <th class="text-left">Prénom du contact</th>
                                            <th class="text-left">Ville</th>
                                            <th class="text-left">Code postal</th>
                                            <th class="text-left">Identifiant Dahub</th>
                                            <th class="text-left">Identifiant Tourinsoft</th>
                                            <th></th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        <tr v-for="provider in providersToImport" :key="provider.name">
                                            <td>{{ provider.nomDeLoffre }}</td>
                                            <td>{{ provider.categorie }}</td>
                                            <td>{{ provider.destinataire }}</td>
                                            <td>{{ provider.telephone }}</td>
                                            <td>{{ provider.nomContact }}</td>
                                            <td>{{ provider.prenomContact }}</td>
                                            <td>{{ provider.ville }}</td>
                                            <td>{{ provider.codePostal }}</td>
                                            <td>{{ provider.idDahub }}</td>
                                            <td>{{ provider.idTourinsoft }}</td>
                                            <td>
                                                <v-tooltip bottom>
                                                    <template v-slot:activator="{ on }">
                                                        <v-btn v-on="on" icon color="primary"
                                                               @click="prepareUpdateContact(provider)">
                                                            <v-icon>edit</v-icon>
                                                        </v-btn>
                                                    </template>
                                                    <span>Modifier le destinataire</span>
                                                </v-tooltip>
                                            </td>
                                        </tr>
                                        </tbody>
                                    </v-simple-table>
                                </v-expansion-panel-content>
                            </v-expansion-panel>
                        </v-expansion-panels>

                        <div class="actions d-flex justify-content-between mt-5"
                             style="bottom: 20px; width: calc(100% - 58px);">

                            <v-btn class="default-button" @click="returnToloadFileStep()">
                                <v-icon>keyboard_arrow_left</v-icon>
                                Sélectionner un fichier
                            </v-btn>
                            <v-btn class="default-button ml-3 load-button" @click="currentStep = 3">
                                Organiser mes {{ providersToImport.length }} fiches
                                <v-icon>keyboard_arrow_right</v-icon>
                            </v-btn>
                        </div>
                    </v-stepper-content>

                    <v-stepper-content step="3">
                        <v-alert text>
                            <p>
                                Afin de vous faciliter l'organisation ou le tri de vos fiches dans Dahub, il est
                                possible dès maintenant de leur associer un <code>tag</code>.
                            </p>
                            <p>
                                Par exemple : vous pourrez plus tard décider d'action spécifiquement dédiées aux fiches
                                taggées <code>adhérents 2020</code>.
                            </p>
                        </v-alert>

                        <v-alert type="info" text>
                            Cette action est <b>optionnelle</b>, vous pourrez taguer vos fiches ultérieurement dans
                            Dahub.
                        </v-alert>

                        <v-combobox outlined
                                    class="w-50"
                                    label="Tags"
                                    multiple
                                    chips
                                    small-chips
                                    :items="tags"
                                    item-text="name"
                                    deletable-chips
                                    v-model="selectedTags"
                                    return-object>
                        </v-combobox>

                        <div class="actions d-flex justify-content-between mt-5">
                            <v-btn class="default-button" @click="currentStep = 2">
                                <v-icon>keyboard_arrow_left</v-icon>
                                Vérifier mes fiches
                            </v-btn>
                            <v-btn class="default-button ml-3 load-button" @click="importProviders">
                                Importer
                                <span class="ml-1 mr-1" v-if="selectedTags.length">et tager</span>
                                {{ providersToImport.length }} fiches
                            </v-btn>
                        </div>
                    </v-stepper-content>
                </v-stepper-items>
            </v-stepper>
        </v-dialog>

        <update-receiver-modale :edit-contact-modal="editContactModal"
                                :selected-contact="selectedContact"
                                :selected-provider="selectedProvider"
                                :no-db="true">
        </update-receiver-modale>
    </div>
</template>

<script>
import { mapState } from "vuex"
import UpdateReceiverModale from "@/components/provider/UpdateReceiverModale"

export default {
    name: "ImportDahub",
    components: { UpdateReceiverModale },
    props: ["importDialog"],
    data() {
        return {
            currentStep: 1,
            file: null,
            rules: [
                value => !value || value.name.substr(value.name.length - 3) === "xls" ||
                  value.name.substr(value.name.length - 4) === "xlsx" ||
                  "Le fichier doit être au format .xls",
            ],
            error: null,
            validFile: false,
            providersToCheck: [],
            providersErrors: [],
            providersToImport: [],
            wrongDahubIds: [],
            panel: [0],
            editContactModal: false,
            selectedContact: null,
            selectedProvider: [],
            selectedTags: [],
            availableCatgoriesLabel: [
                "Activité",
                "Équipement",
                "Hôtellerie de plein air",
                "Hébergement collectif",
                "Patrimoine culturel",
                "Restauration",
                "Commerce et service",
                "Producteur",
                "Hôtellerie",
                "Hébergement locatif",
                "Patrimoine naturel",
            ],
            apiUrl: process.env.VUE_APP_API_URL,
        }
    },
    methods: {
        closeDialog() {
            this.$global.$emit("close-import")
            this.resetState()
            this.file = null
            this.error = null
            this.currentStep = 1
        },
        loadFile(file) {
            if (file !== undefined) {
                this.file = file
                this.validFile = file.name.substr(file.name.length - 3) === "xls" ||
                  file.name.substr(file.name.length - 4) === "xlsx"
            }
        },
        loadProviders() {
            let formData = new FormData
            formData.append("import", this.file)

            this.$global.loading = true
            this.providersErrors = []
            this.wrongDahubIds = []
            this.error = null

            this.$http.post("auth/providers/prepare-import-dahub-providers", formData).then(res => {
                this.$global.loading = false
                this.providersToCheck = res.body.data.providers
                this.error = res.body.data.error
                this.wrongDahubIds = res.body.data.idDahubError

                if (this.error === null) {
                    this.checkProviders()
                    this.currentStep = 2
                }
            }).catch((error) => {
                let body = {
                    message: "Le fichier a été modifié après avoir été chargé. Merci de l'uploader à nouveau.",
                }
                this.$global.$emit("handled-error", { body: body })
                this.resetState()
                this.file = null
                this.error = null
                this.$global.loading = false
            })
        },
        checkProviders() {
            this.providersToCheck.forEach(provider => {
                if (provider.nomDeLoffre === "") {
                    provider.error = {
                        code: 1,
                        label: "Nom manquant",
                    }
                    this.providersErrors.push(provider)
                } else if (provider.destinataire === "") {
                    provider.error = {
                        code: 1,
                        label: "Email manquant",
                    }
                    this.providersErrors.push(provider)
                } else if (provider.categorie === "") {
                    provider.error = {
                        code: 1,
                        label: "Catégorie manquante",
                    }
                    this.providersErrors.push(provider)
                } else if (!this.availableCatgoriesLabel.includes(provider.categorie)) {
                    provider.error = {
                        code: 1,
                        label: "Catégorie non reconnue",
                    }
                    this.providersErrors.push(provider)
                } else if (provider.ville === "") {
                    provider.error = {
                        code: 1,
                        label: "Ville manquante",
                    }
                    this.providersErrors.push(provider)
                }  else if (provider.codePostal === "") {
                    provider.error = {
                        code: 1,
                        label: "Code postal manquant",
                    }
                    this.providersErrors.push(provider)
                } else if (provider.idDahub && this.wrongDahubIds.includes(provider.idDahub)) {
                    provider.error = {
                        code: 1,
                        label: "Identifiant Dahub incorrect",
                    }
                    this.providersErrors.push(provider)
                } else {
                    this.providersToImport.push(provider)
                }
            })
        },
        prepareUpdateContact(provider) {
            this.editContactModal = true
            this.selectedContact = provider.destinataire
            this.selectedProvider = provider
        },
        importProviders() {
            setTimeout(() => {
                let newTags = []
                let tags = []

                this.selectedTags.forEach(tag => {
                    if (typeof tag === "object") {
                        tags.push(tag)
                    } else {
                        newTags.push(tag)
                    }
                })

                let payload = {
                    providers: this.providersToImport,
                    newTags: newTags,
                    tags: tags,
                }

                this.$global.loading = true

                this.$http.post("auth/providers/import-dahub-providers", payload).then(res => {
                    this.selectedTags = []
                    this.resetState()
                    this.file = null
                    this.error = null

                    this.$global.loading = false
                    this.$global.$emit("load-providers")

                    this.$store.dispatch("account/fetchCommunes")
                    this.$store.dispatch("account/fetchCategories")
                    this.$store.dispatch("resource/fetchWebFormStates")

                    this.currentStep = 1
                })
            }, 500)
        },
        returnToloadFileStep() {
            this.currentStep = 1
            this.resetState()
        },
        resetState() {
            this.providersToCheck = []
            this.providersErrors = []
            this.providersToImport = []
            this.wrongDahubIds = []
        }
    },
    computed: {
        ...mapState("account", ["tags"]),
    },
    created() {
        this.$global.$on("close-update-modal", () => {
            this.editContactModal = false
        })

        this.$global.$on("update-email", (email) => {
            this.providersToImport.forEach(provider => {
                if (provider === this.selectedProvider) {
                    provider.destinataire = email
                }
            })
        })
    },
    beforeDestroy() {
        this.$global.$off("update-email")
        this.$global.$off("close-update-modal")
    },
}
</script>